<script setup>
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  title: { type: String, required: true },
  icon: { type: String, default: 'i-lucide-text-cursor-input' },
  field: { type: Object, required: true },
  textOnly: { type: Boolean, default: false },
  showAvailabilityToggle: { type: Boolean, default: false },
});

const emit = defineEmits(['update']);
const { t } = useI18n();

const patch = (key, value) => {
  emit('update', { ...props.field, [key]: value });
};
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="mb-4 flex items-center justify-between gap-3">
      <h3 class="flex items-center gap-2 text-sm font-semibold text-n-slate-12">
        <Icon :icon="icon" class="size-4 shrink-0 text-n-slate-11" />
        {{ title }}
      </h3>
      <Switch
        :model-value="field.enabled !== false"
        :disabled="textOnly"
        @update:model-value="patch('enabled', $event)"
      />
    </div>

    <div v-if="field.enabled !== false" class="space-y-3">
      <Input
        :model-value="field.label || ''"
        :label="t('BOOKING_WIDGETS.LABEL')"
        @update:model-value="patch('label', $event)"
        class="!text-base font-normal"
      />
      <Input
        :model-value="field.help || ''"
        :label="t('BOOKING_WIDGETS.HELP_TEXT')"
        @update:model-value="patch('help', $event)"
        class="!text-base font-normal"
      />
      <label
        class="flex items-center gap-2 text-sm text-n-slate-12"
        :class="{ 'opacity-60': textOnly }"
      >
        <input
          type="checkbox"
          class="rounded border-n-weak"
          :checked="!!field.required"
          :disabled="textOnly"
          @change="patch('required', $event.target.checked)"
        />
        {{ t('BOOKING_WIDGETS.REQUIRED') }}
      </label>
      <label
        v-if="showAvailabilityToggle"
        class="flex items-center gap-2 text-sm text-n-slate-12"
        :class="{ 'opacity-60': textOnly }"
      >
        <input
          type="checkbox"
          class="rounded border-n-weak"
          :checked="field.showAvailability !== false"
          :disabled="textOnly"
          @change="patch('showAvailability', $event.target.checked)"
        />
        {{ t('BOOKING_WIDGETS.SHOW_AVAILABILITY') }}
      </label>
      <div
        v-if="$slots.default"
        class="border-t border-n-weak pt-3"
      >
        <slot />
      </div>
    </div>
  </div>
</template>
